<script setup>
import CreateShortMessageServiceAction from '@frontJs/components/actions/projectManagement/sem/CreateShortMessageServiceAction.vue';
import EyeIcon from '@iconify-vue/ph/eye';
import PencilSimpleIcon from '@iconify-vue/ph/pencil-simple';
import PlusIcon from '@iconify-vue/ph/plus';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import DataTableLayout from '@js/components/tables/DataTableLayout.vue';
import BaseCell from '@js/components/tables/cells/BaseCell.vue';
import HeaderCell from '@js/components/tables/cells/HeaderCell.vue';
import NumberCell from '@js/components/tables/cells/NumberCell.vue';
import PillCell from '@js/components/tables/cells/PillCell.vue';
import TextCell from '@js/components/tables/cells/TextCell.vue';
import { useDataTable } from '@js/utils/dataTable';
import { formatDate } from '@js/utils/formatter';
import { useRoute } from '@js/utils/route';
import { createColumnHelper } from '@tanstack/vue-table';
import { h } from 'vue';

const { tenant, all } = defineProps({
    tenant: {
        type: Object,
        required: false,
    },
    all: {
        type: Boolean,
        required: false,
    },
});

const { route } = useRoute();

const columnHelper = createColumnHelper();

const {
    loading,
    table,
    count,
    globalFilter,
    filters,
    availableFilters,
    setGlobalFilter,
    setFilters,
    globalData,
} = useDataTable({
    columns: [
        columnHelper.accessor(() => null, {
            id: 'tenant.name',
            header: () => 'Client',
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.tenant.name,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'id',
            header: () => 'ID',
            cell: (props) =>
                h(NumberCell, {
                    number: props.row.original.id,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'websiteVariant',
            header: () => 'Site',
            cell: (props) =>
                h(PillCell, {
                    pillValue: props.row.original.websiteVariant.name,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'subject',
            header: () => 'Sujet',
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.subject,
                    length: 160,
                }),
        }),
        columnHelper.display({
            id: 'details',
            header: 'Détails',
            cell: (props) =>
                h(
                    BaseCell,
                    {},
                    {
                        default: () =>
                            props.row.original.link
                                ? h(ActionButton, {
                                      size: 'small',
                                      square: true,
                                      title: 'Voir le SMS',
                                      type: 'primary',
                                      href: props.row.original.link,
                                      target: '_blank',
                                      icon: EyeIcon,
                                  })
                                : null,
                    },
                ),
        }),
        columnHelper.accessor(() => null, {
            id: 'sentAt',
            header: () => 'Envoyé le',
            cell: (props) =>
                props.row.original.sentAt
                    ? h(TextCell, {
                          text: formatDate(props.row.original.sentAt),
                      })
                    : null,
        }),
        columnHelper.display({
            id: 'actions',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Actions',
                    width: 0,
                }),
            cell: (props) =>
                h(
                    BaseCell,
                    {},
                    {
                        default: () =>
                            h(ActionButton, {
                                size: 'small',
                                square: true,
                                title: 'Modifier',
                                href: route(
                                    'front.projectManagement.sem.shortMessageService',
                                    {
                                        tenant: tenant
                                            ? tenant.id
                                            : props.row.original.tenant.id,
                                        id: props.row.original.id,
                                    },
                                ),
                                icon: PencilSimpleIcon,
                            }),
                    },
                ),
        }),
    ],
    url: route('front.projectManagement.sem.postShortMessageServices', {
        tenant: tenant ? tenant.id : null,
        all,
    }),
    columnPinning: {
        right: ['actions'],
    },
    columnVisibility: {
        'tenant.name': !tenant,
        id: !!tenant,
    },
});
</script>

<template>
    <DataTableLayout
        :table="table"
        :count="count"
        :global-filter="globalFilter"
        :filters="filters"
        :available-filters="availableFilters"
        :set-filters="setFilters"
        :set-global-filter="setGlobalFilter"
        :loading="loading"
    >
        <template #actions>
            <ActionButton
                v-if="tenant"
                size="small"
                :href="
                    route('front.projectManagement.sem.shortMessageService', {
                        tenant: tenant.id,
                    })
                "
                :left-icon="PlusIcon"
            >
                Ajouter
            </ActionButton>

            <CreateShortMessageServiceAction
                v-else
                :tenants="globalData.tenants"
            >
                <template #trigger>
                    <ActionButton
                        size="small"
                        :left-icon="PlusIcon"
                        type="primary"
                    >
                        Ajouter
                    </ActionButton>
                </template>
            </CreateShortMessageServiceAction>
        </template>
    </DataTableLayout>
</template>
